import { createContext, useState, useEffect, useMemo, useContext, useRef } from 'react';
import { ThemeProvider, CssBaseline, Box, Button, CircularProgress, Breadcrumbs as Breadcrumbs$1, Typography, Link, Chip, Card, CardContent, Paper, Slide, IconButton, Dialog, DialogTitle, DialogContent, Alert, DialogActions, TextField, InputAdornment, TableContainer, Table, Tooltip, TableBody, TableRow, TableCell, TableHead, List, ListItem, ListItemText, ListItemSecondaryAction, FormControlLabel, Checkbox, FormControl, Select, MenuItem, ListItemButton, ListItemIcon, InputLabel } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { NavigateNext, Inbox, Warning, Error as Error$1, CheckCircle, Info, Close, Delete, Add, Refresh, Storage, Build, Edit, VisibilityOff, Visibility, ArrowBack, Folder, Description, Schedule, PlayArrow, ContentCopy, ArrowDownward, ArrowUpward } from '@mui/icons-material';

var baseThemeOptions = {
  typography: {
    fontFamily: [
      "Inter",
      "system-ui",
      "-apple-system",
      "Segoe UI",
      "Roboto",
      "Helvetica Neue",
      "Arial",
      "Noto Sans",
      "Liberation Sans",
      "Apple Color Emoji",
      "Segoe UI Emoji"
    ].join(","),
    h1: { fontWeight: 400 },
    h2: { fontWeight: 400, fontSize: "2.5rem" },
    h3: { fontWeight: 500 },
    h4: { fontWeight: 500 },
    button: { textTransform: "none", fontWeight: 600 }
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "var(--palette-primary-50)",
          color: "#FFFFFF"
        }
      }
    },
    // Ensure page titles/headings are always white across the app UI.
    // Some pages explicitly set `color="text.primary"` and some themes may have non-white text.primary;
    // overriding the heading variants keeps titles consistently readable on our dark surfaces.
    MuiTypography: {
      styleOverrides: {
        h1: { color: "#FFFFFF" },
        h2: { color: "#FFFFFF" },
        h3: { color: "#FFFFFF" },
        h4: { color: "#FFFFFF" },
        h5: { color: "#FFFFFF" },
        h6: { color: "#FFFFFF" }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid var(--palette-primary-200)",
          backgroundColor: "var(--palette-primary-75)"
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 6, paddingInline: 16, paddingBlock: 10 },
        // Primary fills: use a lighter surface than palette.primary.main so labels stay crisp on dim displays.
        containedPrimary: {
          backgroundColor: "var(--palette-primary-400)",
          color: "#FFFFFF",
          ":hover": {
            backgroundColor: "var(--palette-primary-500)",
            filter: "none"
          }
        },
        // Outlined + text: default MUI uses dark primary blue for label/border on navy cards — poor contrast.
        outlined: {
          borderColor: "rgba(255,255,255,0.5)",
          color: "rgba(255,255,255,0.95)",
          "&:hover": {
            borderColor: "rgba(255,255,255,0.72)",
            backgroundColor: "rgba(255,255,255,0.1)"
          }
        },
        outlinedPrimary: {
          borderColor: "rgba(134, 190, 223, 0.75)",
          color: "#F2F9FF",
          "&:hover": {
            borderColor: "rgba(134, 190, 223, 0.95)",
            backgroundColor: "rgba(134, 190, 223, 0.12)"
          }
        },
        outlinedSecondary: {
          borderColor: "rgba(143, 167, 60, 0.85)",
          color: "#F7FAF0",
          "&:hover": {
            borderColor: "rgba(167, 190, 90, 1)",
            backgroundColor: "rgba(143, 167, 60, 0.14)"
          }
        },
        outlinedError: {
          borderColor: "rgba(232, 143, 136, 0.9)",
          color: "#FFEFED",
          "&:hover": {
            borderColor: "#F0A099",
            backgroundColor: "rgba(172, 63, 54, 0.18)"
          }
        },
        text: {
          color: "rgba(255,255,255,0.88)",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" }
        },
        textPrimary: {
          color: "rgba(190, 220, 245, 0.95)",
          "&:hover": { backgroundColor: "rgba(134, 190, 223, 0.12)" }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "rgba(255,255,255,0.68)",
          "&:hover": { color: "rgba(255,255,255,0.9)" },
          "&.Mui-selected": {
            color: "#FFFFFF"
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: "rgba(134, 190, 223, 0.95)",
          height: 3,
          borderRadius: "3px 3px 0 0"
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: "rgba(255,255,255,0.78)",
          borderColor: "rgba(255,255,255,0.38)",
          "&.Mui-selected": {
            color: "#FFFFFF",
            backgroundColor: "rgba(255,255,255,0.14)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" }
          },
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.08)"
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--palette-primary-75)",
          borderBottom: "1px solid var(--palette-primary-200)"
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--palette-primary-75)",
          border: "1px solid var(--palette-primary-200)"
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: "#0C1300",
          color: "#FFFFFF"
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: { color: "#FFFFFF", ":hover": { color: "#E0E0E0" } }
      }
    },
    MuiTextField: {
      defaultProps: { variant: "outlined" },
      styleOverrides: {
        root: {
          // Use elevated surface tone, not `--palette-primary-50`: frontend palette maps 50 to a light tint
          // while MUI keeps input text white (text.primary); light 50 × white labels was unreadable.
          "& .MuiOutlinedInput-root": {
            backgroundColor: "var(--palette-primary-75)",
            "& fieldset": { borderColor: "var(--palette-primary-200)" },
            "&:hover fieldset": { borderColor: "var(--palette-primary-300)" },
            "&.Mui-focused fieldset": { borderColor: "#8FA73C" }
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "var(--palette-primary-100)",
          border: "1px solid var(--palette-primary-200)"
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: { color: "#E9ECEF", borderWidth: 1, borderStyle: "solid" },
        standardSuccess: {
          backgroundColor: "#0C1300",
          borderColor: "#1A2500",
          "& .MuiAlert-icon": { color: "#8FA73C" }
        },
        standardError: {
          backgroundColor: "#1B0201",
          borderColor: "#2C0502",
          "& .MuiAlert-icon": { color: "#AC3F36" }
        },
        standardWarning: {
          backgroundColor: "#130D01",
          borderColor: "#241702",
          "& .MuiAlert-icon": { color: "#A57030" }
        },
        standardInfo: {
          backgroundColor: "#001123",
          borderColor: "#00224A",
          "& .MuiAlert-icon": { color: "#4187B5" }
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--palette-primary-100)"
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--palette-primary-100)"
        }
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--palette-primary-100)"
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--palette-primary-75)",
          padding: "8px 16px",
          "&:nth-of-type(even)": {
            backgroundColor: "var(--palette-primary-75)"
          },
          "&:nth-of-type(odd)": {
            backgroundColor: "var(--palette-primary-75)"
          },
          "&:hover": {
            backgroundColor: "var(--palette-primary-100)"
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          border: "none",
          padding: "8px 16px",
          fontSize: "0.875rem"
        },
        head: {
          backgroundColor: "var(--palette-primary-100)",
          color: "#FFFFFF",
          fontWeight: 600,
          fontSize: "0.875rem"
        }
      }
    }
  }
};
var palettes = {
  "Evergreen Dark": {
    mode: "dark",
    primary: { main: "#014787" },
    secondary: { main: "#8FA73C" },
    success: { main: "#63A3CA" },
    warning: { main: "#AC3F36", light: "#E88F88" },
    error: { main: "#AC3F36" },
    background: { default: "#001123", paper: "#001A36" },
    text: { primary: "#FFFFFF", secondary: "#FFFFFF" },
    divider: "#002F61"
  },
  "Midnight Maritime": {
    mode: "dark",
    primary: { main: "#014787" },
    secondary: { main: "#8FA73C" },
    success: { main: "#63A3CA" },
    warning: { main: "#AC3F36", light: "#E88F88" },
    error: { main: "#AC3F36" },
    background: { default: "#001123", paper: "#001A36" },
    text: { primary: "#FFFFFF", secondary: "#FFFFFF" },
    divider: "#002F61"
  },
  "Nord Mist": {
    mode: "dark",
    primary: { main: "#86BEDF" },
    secondary: { main: "#8FA73C" },
    success: { main: "#63A3CA" },
    warning: { main: "#AC3F36", light: "#E88F88" },
    error: { main: "#AC3F36" },
    background: { default: "#001123", paper: "#001A36" },
    text: { primary: "#FFFFFF", secondary: "#FFFFFF" },
    divider: "#002F61"
  },
  "Desert Dusk": {
    mode: "dark",
    primary: { main: "#A57030" },
    secondary: { main: "#8FA73C" },
    success: { main: "#63A3CA" },
    warning: { main: "#AC3F36", light: "#E88F88" },
    error: { main: "#AC3F36" },
    background: { default: "#001123", paper: "#001A36" },
    text: { primary: "#FFFFFF", secondary: "#FFFFFF" },
    divider: "#002F61"
  },
  "Neon Terminal": {
    mode: "dark",
    primary: { main: "#8FA73C" },
    secondary: { main: "#86BEDF" },
    success: { main: "#63A3CA" },
    warning: { main: "#AC3F36", light: "#E88F88" },
    error: { main: "#AC3F36" },
    background: { default: "#001123", paper: "#001A36" },
    text: { primary: "#FFFFFF", secondary: "#FFFFFF" },
    divider: "#002F61"
  },
  "Solar Ink": {
    mode: "dark",
    primary: { main: "#4187B5" },
    secondary: { main: "#8FA73C" },
    success: { main: "#63A3CA" },
    warning: { main: "#AC3F36", light: "#E88F88" },
    error: { main: "#AC3F36" },
    background: { default: "#001123", paper: "#001A36" },
    text: { primary: "#FFFFFF", secondary: "#FFFFFF" },
    divider: "#002F61"
  },
  "Berry Noir": {
    mode: "dark",
    primary: { main: "#014787" },
    secondary: { main: "#8FA73C" },
    success: { main: "#63A3CA" },
    warning: { main: "#AC3F36", light: "#E88F88" },
    error: { main: "#AC3F36" },
    background: { default: "#001123", paper: "#001A36" },
    text: { primary: "#FFFFFF", secondary: "#FFFFFF" },
    divider: "#002F61"
  },
  "Graphite Pro": {
    mode: "dark",
    primary: { main: "#86BEDF" },
    secondary: { main: "#8FA73C" },
    success: { main: "#63A3CA" },
    warning: { main: "#AC3F36", light: "#E88F88" },
    error: { main: "#AC3F36" },
    background: { default: "#001123", paper: "#001A36" },
    text: { primary: "#FFFFFF", secondary: "#FFFFFF" },
    divider: "#002F61"
  }
};
var themes = Object.fromEntries(
  Object.entries(palettes).map(([name, palette]) => [
    name,
    createTheme({ ...baseThemeOptions, palette })
  ])
);
function getTheme(name) {
  return themes[name];
}
var themeNames = Object.keys(themes);
var DEFAULT_THEME_NAME = "Evergreen Dark";
var DEFAULT_THEME_STORAGE_KEY = "veilstream-theme";

// src/utils/storage.ts
var localStorageAdapter = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
    }
  }
};
var noOpStorageAdapter = {
  getItem: () => null,
  setItem: () => {
  },
  removeItem: () => {
  }
};
var VeilStreamThemeContext = createContext(void 0);
function VeilStreamThemeProvider({
  children,
  storage = noOpStorageAdapter,
  storageKey = DEFAULT_THEME_STORAGE_KEY,
  defaultTheme = DEFAULT_THEME_NAME,
  includeCssBaseline = true
}) {
  const [currentTheme, setCurrentTheme] = useState(defaultTheme);
  useEffect(() => {
    const savedTheme = storage.getItem(storageKey);
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, [storage, storageKey]);
  const setTheme = (themeName) => {
    setCurrentTheme(themeName);
    storage.setItem(storageKey, themeName);
  };
  const contextValue = useMemo(
    () => ({
      currentTheme,
      setTheme,
      availableThemes: themeNames,
      theme: themes[currentTheme]
    }),
    [currentTheme]
  );
  return /* @__PURE__ */ jsx(VeilStreamThemeContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsxs(ThemeProvider, { theme: themes[currentTheme], children: [
    includeCssBaseline && /* @__PURE__ */ jsx(CssBaseline, {}),
    children
  ] }) });
}
function useVeilStreamTheme() {
  const context = useContext(VeilStreamThemeContext);
  if (context === void 0) {
    throw new Error("useVeilStreamTheme must be used within a VeilStreamThemeProvider");
  }
  return context;
}

// src/utils/logFormatting.ts
function stripIsoTimestampPrefix(message) {
  const isoTimestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?\s+/;
  return message.replace(isoTimestampRegex, "").trim();
}
function detectLogLevel(message) {
  const cleanMessage = stripIsoTimestampPrefix(message).toLowerCase();
  const errorPatterns = [
    /\berror\b/,
    // "error" as a word
    /\bfatal\b/,
    // "fatal" as a word
    /\bexception\b/,
    // "exception" as a word
    /\bfailed\b/,
    // "failed" as a word
    /\bfailure\b/,
    // "failure" as a word
    /\bpanic\b/,
    // "panic" as a word
    /\bcrash\b/,
    // "crash" as a word
    /\btimeout\b/,
    // "timeout" as a word
    /\bdenied\b/,
    // "denied" as a word
    /\brejected\b/
    // "rejected" as a word
  ];
  const warningPatterns = [
    /\bwarn\b/,
    // "warn" as a word
    /\bwarning\b/,
    // "warning" as a word
    /\bdeprecated\b/,
    // "deprecated" as a word
    /\bnotice\b/
    // "notice" as a word
  ];
  const debugPatterns = [
    /\bdebug\b/,
    // "debug" as a word
    /\btrace\b/
    // "trace" as a word
  ];
  for (const pattern of errorPatterns) {
    if (pattern.test(cleanMessage)) {
      return "error";
    }
  }
  for (const pattern of warningPatterns) {
    if (pattern.test(cleanMessage)) {
      return "warning";
    }
  }
  for (const pattern of debugPatterns) {
    if (pattern.test(cleanMessage)) {
      return "debug";
    }
  }
  return "info";
}
function getLogLevelColor(level) {
  switch (level.toLowerCase()) {
    case "error":
      return "#f44336";
    // Red
    case "warn":
    case "warning":
      return "#ff9800";
    // Orange
    case "info":
      return "#2196f3";
    // Blue
    case "debug":
      return "#9e9e9e";
    // Grey
    case "event":
      return "#ffc107";
    // Amber/Yellow
    default:
      return "#ffffff";
  }
}
function Inline({ children, gap = 1, wrap = false }) {
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "span",
      sx: {
        display: "inline-flex",
        alignItems: "center",
        gap,
        flexWrap: wrap ? "wrap" : "nowrap"
      },
      children
    }
  );
}
function StandardButton({
  variant = "primary",
  loading = false,
  size = "medium",
  children,
  disabled,
  startIcon,
  endIcon,
  sx,
  ...props
}) {
  const muiVariant = variant === "primary" ? "contained" : variant === "secondary" ? "outlined" : "text";
  const muiSize = size === "medium" ? void 0 : size;
  return /* @__PURE__ */ jsx(
    Button,
    {
      variant: muiVariant,
      size: muiSize,
      color: "primary",
      disabled: disabled || loading,
      startIcon: loading ? /* @__PURE__ */ jsx(CircularProgress, { size: 16, color: "inherit" }) : startIcon,
      endIcon,
      sx: {
        textTransform: "none",
        borderRadius: "6px",
        fontWeight: 600,
        ...sx
      },
      ...props,
      children
    }
  );
}
function Breadcrumbs({
  items,
  LinkComponent,
  separator = /* @__PURE__ */ jsx(NavigateNext, { fontSize: "small" }),
  showMargin = true
}) {
  return /* @__PURE__ */ jsx(Box, { sx: { mb: showMargin ? 2 : 0 }, children: /* @__PURE__ */ jsx(
    Breadcrumbs$1,
    {
      separator,
      "aria-label": "breadcrumb",
      sx: {
        "& .MuiBreadcrumbs-separator": {
          color: "text.secondary"
        }
      },
      children: items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isCurrent = item.current || isLast;
        if (isCurrent) {
          return /* @__PURE__ */ jsx(
            Typography,
            {
              color: "text.primary",
              sx: {
                fontWeight: isLast ? 600 : 400,
                fontSize: "0.875rem"
              },
              children: item.label
            },
            index
          );
        }
        const linkProps = {
          key: index,
          sx: {
            color: "text.secondary",
            textDecoration: "none",
            fontSize: "0.875rem",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
              color: "primary.main"
            }
          }
        };
        if (item.onClick) {
          linkProps.component = "button";
          linkProps.onClick = item.onClick;
        } else if (LinkComponent && item.href) {
          linkProps.component = LinkComponent;
          linkProps.to = item.href;
          linkProps.href = item.href;
        } else if (item.href) {
          linkProps.href = item.href;
        } else {
          linkProps.component = "button";
        }
        return /* @__PURE__ */ jsx(Link, { ...linkProps, children: item.label });
      })
    }
  ) });
}
var defaultStatusColors = {
  // Success states
  Running: "success",
  Succeeded: "success",
  Active: "success",
  Ready: "success",
  // In-progress states
  Pending: "default",
  Creating: "default",
  Deleting: "default",
  Terminating: "default",
  // Warning states
  Warning: "warning",
  Unknown: "warning",
  NotReady: "warning",
  // Error states
  Failed: "error",
  Error: "error",
  CrashLoopBackOff: "error",
  Inactive: "error"
};
function StatusChip({ status, getColor, variant = "filled", size = "small", ...props }) {
  const color = getColor ? getColor(status) : defaultStatusColors[status] || defaultStatusColors[status.toLowerCase()] || "default";
  return /* @__PURE__ */ jsx(Chip, { label: status, color, variant, size, ...props });
}
function StatCard({
  label,
  value,
  subtitle,
  icon,
  valueVariant = "h4",
  valueColor,
  formatValue
}) {
  const displayValue = typeof value === "number" && formatValue ? formatValue(value) : value;
  return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(CardContent, { children: [
    icon && /* @__PURE__ */ jsx(Box, { sx: { mb: 1, display: "flex", alignItems: "center" }, children: icon }),
    /* @__PURE__ */ jsx(Typography, { color: "textSecondary", gutterBottom: true, children: label }),
    /* @__PURE__ */ jsx(
      Typography,
      {
        variant: valueVariant,
        component: "div",
        sx: valueColor ? { color: valueColor } : void 0,
        children: displayValue
      }
    ),
    subtitle && /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "textSecondary", children: subtitle })
  ] }) });
}
var sizeConfig = {
  small: {
    padding: 2,
    iconSize: 40,
    titleVariant: "body1",
    messageVariant: "body2",
    gap: 1
  },
  medium: {
    padding: 4,
    iconSize: 48,
    titleVariant: "h6",
    messageVariant: "body2",
    gap: 2
  },
  large: {
    padding: 6,
    iconSize: 64,
    titleVariant: "h5",
    messageVariant: "body1",
    gap: 3
  }
};
function EmptyState({
  icon = /* @__PURE__ */ jsx(Inbox, {}),
  title,
  message,
  action,
  size = "medium",
  paper = true,
  sx
}) {
  const config = sizeConfig[size];
  const content = /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        textAlign: "center",
        py: config.padding,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: config.gap,
        ...sx
      },
      children: [
        icon && /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              color: "text.secondary",
              opacity: 0.5,
              fontSize: config.iconSize,
              "& svg": {
                fontSize: config.iconSize
              }
            },
            children: icon
          }
        ),
        title && /* @__PURE__ */ jsx(Typography, { variant: config.titleVariant, color: "text.secondary", fontWeight: 500, children: title }),
        /* @__PURE__ */ jsx(Typography, { variant: config.messageVariant, color: "text.secondary", children: message }),
        action && /* @__PURE__ */ jsx(Box, { sx: { mt: 1 }, children: action })
      ]
    }
  );
  if (paper) {
    return /* @__PURE__ */ jsx(Paper, { children: content });
  }
  return content;
}
var sizeMap = {
  small: 24,
  medium: 40,
  large: 56
};
function LoadingState({
  message,
  size = "medium",
  minHeight,
  center = true,
  py = 4,
  sx
}) {
  const spinnerSize = typeof size === "number" ? size : sizeMap[size];
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: center ? "center" : "flex-start",
        py,
        minHeight,
        gap: 2,
        ...sx
      },
      children: [
        /* @__PURE__ */ jsx(CircularProgress, { size: spinnerSize }),
        message && /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", children: message })
      ]
    }
  );
}
var severityConfig = {
  info: {
    icon: Info,
    color: "info"
  },
  success: {
    icon: CheckCircle,
    color: "success"
  },
  error: {
    icon: Error$1,
    color: "error"
  },
  warning: {
    icon: Warning,
    color: "warning"
  }
};
function Toast({
  open,
  onClose,
  title,
  message,
  severity = "info",
  position = "top",
  autoHideDuration = 0,
  icon,
  showCloseButton = true
}) {
  useEffect(() => {
    if (open && autoHideDuration && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [open, autoHideDuration, onClose]);
  const config = severityConfig[severity];
  const IconComponent = config.icon;
  const slideDirection = position === "top" ? "down" : "up";
  return /* @__PURE__ */ jsx(Slide, { direction: slideDirection, in: open, mountOnEnter: true, unmountOnExit: true, children: /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        position: "fixed",
        [position]: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1300,
        maxWidth: 600,
        width: "90%"
      },
      children: /* @__PURE__ */ jsxs(
        Paper,
        {
          sx: {
            p: 2,
            backgroundColor: `${config.color}.main`,
            color: `${config.color}.contrastText`,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: `${config.color}.dark`,
            boxShadow: 3
          },
          children: [
            /* @__PURE__ */ jsx(Box, { sx: { mt: 0.25, flexShrink: 0 }, children: icon || /* @__PURE__ */ jsx(IconComponent, {}) }),
            /* @__PURE__ */ jsxs(Box, { sx: { flex: 1 }, children: [
              /* @__PURE__ */ jsx(Typography, { variant: "subtitle1", fontWeight: "bold", gutterBottom: true, children: title }),
              /* @__PURE__ */ jsx(Typography, { variant: "body2", children: message })
            ] }),
            showCloseButton && /* @__PURE__ */ jsx(
              IconButton,
              {
                size: "small",
                onClick: onClose,
                sx: {
                  color: `${config.color}.contrastText`,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)"
                  },
                  flexShrink: 0
                },
                children: /* @__PURE__ */ jsx(Close, {})
              }
            )
          ]
        }
      )
    }
  ) });
}
function LogLine({
  message,
  timestamp,
  level,
  service,
  container,
  isEvent = false,
  formatTimestamp = (ts) => {
    if (!ts) return "N/A";
    const date = new Date(ts);
    return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleTimeString();
  },
  showBorder = true
}) {
  const detectedLevel = level || detectLogLevel(message);
  const displayLevel = isEvent ? "EVENT" : detectedLevel.toUpperCase();
  const levelColor = isEvent ? getLogLevelColor("event") : getLogLevelColor(detectedLevel);
  const cleanMessage = stripIsoTimestampPrefix(message);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        py: 0.25,
        borderBottom: showBorder ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.05)"
        },
        // Special styling for system events
        ...isEvent && {
          backgroundColor: "rgba(255, 193, 7, 0.1)",
          borderLeft: "3px solid #ffc107",
          pl: 1
        }
      },
      children: [
        /* @__PURE__ */ jsx(
          Typography,
          {
            variant: "caption",
            sx: {
              color: "rgba(255, 255, 255, 0.6)",
              fontFamily: "monospace",
              minWidth: 60,
              fontSize: "0.7rem",
              flexShrink: 0
            },
            children: formatTimestamp(timestamp)
          }
        ),
        /* @__PURE__ */ jsx(
          Chip,
          {
            label: displayLevel,
            size: "small",
            sx: {
              backgroundColor: levelColor,
              color: isEvent ? "#000" : "white",
              fontSize: "0.6rem",
              height: 16,
              minWidth: 40,
              flexShrink: 0,
              fontWeight: isEvent ? "bold" : "normal"
            }
          }
        ),
        service && /* @__PURE__ */ jsx(
          Chip,
          {
            label: service,
            size: "small",
            variant: "outlined",
            sx: {
              color: "rgba(255, 255, 255, 0.7)",
              borderColor: "rgba(255, 255, 255, 0.3)",
              fontSize: "0.6rem",
              height: 16,
              flexShrink: 0,
              fontFamily: "monospace"
            }
          }
        ),
        container && /* @__PURE__ */ jsx(
          Chip,
          {
            label: container,
            size: "small",
            variant: "outlined",
            sx: {
              color: "rgba(255, 193, 7, 0.9)",
              borderColor: "rgba(255, 193, 7, 0.5)",
              fontSize: "0.6rem",
              height: 16,
              flexShrink: 0
            }
          }
        ),
        /* @__PURE__ */ jsx(
          Typography,
          {
            sx: {
              color: isEvent ? "#ffc107" : "white",
              fontFamily: "monospace",
              fontSize: "0.8rem",
              wordBreak: "break-word",
              flex: 1,
              fontWeight: isEvent ? "bold" : "normal"
            },
            children: cleanMessage
          }
        )
      ]
    }
  );
}
function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor = "primary",
  loading = false,
  severity = "warning",
  showWarning = true,
  children
}) {
  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };
  return /* @__PURE__ */ jsxs(Dialog, { open, onClose, maxWidth: "sm", fullWidth: true, children: [
    /* @__PURE__ */ jsx(DialogTitle, { children: /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
      showWarning && /* @__PURE__ */ jsx(Warning, { color: severity }),
      title
    ] }) }),
    /* @__PURE__ */ jsxs(DialogContent, { children: [
      showWarning && /* @__PURE__ */ jsx(Alert, { severity, sx: { mb: 2 }, children: /* @__PURE__ */ jsx(Typography, { variant: "body2", children: message }) }),
      !showWarning && /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { mb: 2 }, children: message }),
      children
    ] }),
    /* @__PURE__ */ jsxs(DialogActions, { children: [
      /* @__PURE__ */ jsx(Button, { onClick: onClose, disabled: loading, children: cancelText }),
      /* @__PURE__ */ jsx(
        Button,
        {
          onClick: handleConfirm,
          color: confirmColor,
          variant: "contained",
          disabled: loading,
          startIcon: loading ? /* @__PURE__ */ jsx(CircularProgress, { size: 16 }) : void 0,
          children: loading ? "Processing..." : confirmText
        }
      )
    ] })
  ] });
}
function TextInputModal({
  open,
  onClose,
  onSave,
  title,
  description,
  label,
  placeholder,
  initialValue = "",
  required = false,
  multiline = false,
  maxLength,
  validate,
  saveButtonText,
  submitButtonText,
  cancelButtonText = "Cancel",
  helperText,
  rows = 4,
  loading = false
}) {
  const effectiveSaveButtonText = saveButtonText ?? submitButtonText ?? "Save";
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setError(null);
    }
  }, [open, initialValue]);
  const handleSave = () => {
    const trimmedValue = value.trim();
    if (required && !trimmedValue) {
      setError("This field is required");
      return;
    }
    if (validate) {
      const validationError = validate(trimmedValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    onSave(trimmedValue);
    onClose();
  };
  const handleClose = () => {
    setValue(initialValue);
    setError(null);
    onClose();
  };
  const handleChange = (e) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) {
      return;
    }
    setValue(newValue);
    setError(null);
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleSave();
    }
  };
  return /* @__PURE__ */ jsxs(Dialog, { open, onClose: handleClose, maxWidth: "sm", fullWidth: true, children: [
    /* @__PURE__ */ jsx(DialogTitle, { children: title }),
    /* @__PURE__ */ jsxs(DialogContent, { children: [
      description && /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 3 }, children: description }),
      /* @__PURE__ */ jsx(Box, { sx: { mt: 2 }, children: /* @__PURE__ */ jsx(
        TextField,
        {
          fullWidth: true,
          label,
          placeholder,
          value,
          onChange: handleChange,
          onKeyPress: handleKeyPress,
          error: !!error,
          helperText: error || (maxLength ? `${value.length}/${maxLength}` : helperText),
          multiline,
          rows: multiline ? rows : 1,
          required,
          autoFocus: true,
          inputProps: {
            maxLength
          }
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs(DialogActions, { children: [
      /* @__PURE__ */ jsx(Button, { onClick: handleClose, children: cancelButtonText }),
      /* @__PURE__ */ jsx(Button, { onClick: handleSave, variant: "contained", color: "primary", disabled: loading || required && !value.trim(), children: loading ? "Saving..." : effectiveSaveButtonText })
    ] })
  ] });
}
function ListManagerModal({
  open,
  onClose,
  onSave,
  title,
  description,
  items,
  placeholder = "Add new item...",
  emptyMessage = "No items",
  maxItems,
  validateItem,
  normalizeCase = true
}) {
  const [localItems, setLocalItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [error, setError] = useState(null);
  useEffect(() => {
    if (open) {
      setLocalItems([...items]);
      setNewItem("");
      setError(null);
    }
  }, [open, items]);
  const normalizeItem = (item) => {
    return normalizeCase ? item.toLowerCase() : item;
  };
  const handleAddItem = () => {
    const trimmedItem = newItem.trim();
    if (!trimmedItem) {
      setError("Item cannot be empty");
      return;
    }
    const normalizedItem = normalizeItem(trimmedItem);
    const normalizedList = localItems.map(normalizeItem);
    if (normalizedList.includes(normalizedItem)) {
      setError("Item already exists");
      return;
    }
    if (maxItems && localItems.length >= maxItems) {
      setError(`Maximum ${maxItems} items allowed`);
      return;
    }
    if (validateItem) {
      const validationError = validateItem(trimmedItem);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setLocalItems((prev) => [...prev, normalizedItem]);
    setNewItem("");
    setError(null);
  };
  const handleRemoveItem = (itemToRemove) => {
    setLocalItems((prev) => prev.filter((item) => item !== itemToRemove));
  };
  const handleSave = () => {
    onSave(localItems);
    onClose();
  };
  const handleClose = () => {
    setLocalItems([...items]);
    setNewItem("");
    setError(null);
    onClose();
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddItem();
    }
  };
  const isDuplicate = Boolean(newItem.trim() && localItems.map(normalizeItem).includes(normalizeItem(newItem.trim())));
  return /* @__PURE__ */ jsxs(Dialog, { open, onClose: handleClose, maxWidth: "sm", fullWidth: true, children: [
    /* @__PURE__ */ jsx(DialogTitle, { children: title }),
    /* @__PURE__ */ jsxs(DialogContent, { children: [
      description && /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 3 }, children: description }),
      /* @__PURE__ */ jsxs(Box, { sx: { mb: 3 }, children: [
        /* @__PURE__ */ jsxs(Typography, { variant: "subtitle2", gutterBottom: true, children: [
          "Current Items ",
          maxItems && `(${localItems.length}/${maxItems})`
        ] }),
        /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 1, mb: 2, minHeight: 40 }, children: [
          localItems.map((item) => /* @__PURE__ */ jsx(
            Chip,
            {
              label: item,
              size: "small",
              color: "secondary",
              onDelete: () => handleRemoveItem(item),
              deleteIcon: /* @__PURE__ */ jsx(Delete, {})
            },
            item
          )),
          localItems.length === 0 && /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", children: emptyMessage })
        ] })
      ] }),
      /* @__PURE__ */ jsx(Box, { sx: { display: "flex", gap: 1, alignItems: "flex-start" }, children: /* @__PURE__ */ jsx(
        TextField,
        {
          fullWidth: true,
          size: "small",
          placeholder,
          value: newItem,
          onChange: (e) => {
            setNewItem(e.target.value);
            setError(null);
          },
          onKeyPress: handleKeyPress,
          error: !!error,
          helperText: error,
          autoFocus: true,
          InputProps: {
            endAdornment: /* @__PURE__ */ jsx(InputAdornment, { position: "end", children: /* @__PURE__ */ jsx(IconButton, { size: "small", onClick: handleAddItem, disabled: !newItem.trim() || isDuplicate, children: /* @__PURE__ */ jsx(Add, {}) }) })
          }
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs(DialogActions, { children: [
      /* @__PURE__ */ jsx(Button, { onClick: handleClose, children: "Cancel" }),
      /* @__PURE__ */ jsx(Button, { onClick: handleSave, variant: "contained", color: "primary", children: "Save" })
    ] })
  ] });
}
function MultiFieldFormModal({
  open,
  onClose,
  onSubmit,
  title,
  description,
  fields,
  initialData = {},
  loading = false,
  submitButtonText = "Submit",
  cancelButtonText = "Cancel",
  maxWidth = "sm"
}) {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (justOpened) {
      const initialValues = {};
      fields.forEach((field) => {
        if (initialData[field.name] !== void 0) {
          initialValues[field.name] = initialData[field.name];
        } else if (field.defaultValue !== void 0) {
          initialValues[field.name] = field.defaultValue;
        } else if (field.type === "checkbox") {
          initialValues[field.name] = false;
        } else if (field.type === "multiselect") {
          initialValues[field.name] = [];
        } else {
          initialValues[field.name] = "";
        }
      });
      setFormData(initialValues);
      setErrors({});
      setTouched({});
    }
  }, [open, fields, initialData]);
  const validateField = (field, value) => {
    if (field.required) {
      if (field.type === "checkbox") {
        if (!value) return `${field.label} is required`;
      } else if (field.type === "multiselect") {
        if (!value || value.length === 0) return `${field.label} is required`;
      } else {
        if (!value || typeof value === "string" && !value.trim()) {
          return `${field.label} is required`;
        }
      }
    }
    if (field.validate) {
      return field.validate(value, formData);
    }
    return null;
  };
  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (touched[fieldName]) {
      const field = fields.find((f) => f.name === fieldName);
      if (field) {
        const error = validateField(field, value);
        setErrors((prev) => ({ ...prev, [fieldName]: error || "" }));
      }
    }
  };
  const handleFieldBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const field = fields.find((f) => f.name === fieldName);
    if (field) {
      const error = validateField(field, formData[fieldName]);
      setErrors((prev) => ({ ...prev, [fieldName]: error || "" }));
    }
  };
  const handleSubmit = async () => {
    const newErrors = {};
    let hasErrors = false;
    fields.forEach((field) => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
        hasErrors = true;
      }
    });
    setErrors(newErrors);
    setTouched(
      fields.reduce((acc, field) => ({ ...acc, [field.name]: true }), {})
    );
    if (hasErrors) return;
    await onSubmit(formData);
  };
  const renderField = (field) => {
    const value = formData[field.name] ?? "";
    const error = touched[field.name] && errors[field.name];
    switch (field.type) {
      case "text":
      case "password":
      case "email":
      case "number":
        return /* @__PURE__ */ jsx(
          TextField,
          {
            fullWidth: true,
            label: field.label,
            type: field.type,
            value,
            onChange: (e) => handleFieldChange(field.name, e.target.value),
            onBlur: () => handleFieldBlur(field.name),
            error: !!error,
            helperText: error || field.helperText,
            placeholder: field.placeholder,
            required: field.required,
            disabled: field.disabled || loading,
            inputProps: {
              maxLength: field.maxLength,
              min: field.min,
              max: field.max
            }
          },
          field.name
        );
      case "textarea":
        return /* @__PURE__ */ jsx(
          TextField,
          {
            fullWidth: true,
            multiline: true,
            rows: field.rows || 3,
            label: field.label,
            value,
            onChange: (e) => handleFieldChange(field.name, e.target.value),
            onBlur: () => handleFieldBlur(field.name),
            error: !!error,
            helperText: error || field.helperText,
            placeholder: field.placeholder,
            required: field.required,
            disabled: field.disabled || loading,
            inputProps: {
              maxLength: field.maxLength
            }
          },
          field.name
        );
      case "select":
        return /* @__PURE__ */ jsxs(
          FormControl,
          {
            fullWidth: true,
            error: !!error,
            required: field.required,
            disabled: field.disabled || loading,
            children: [
              /* @__PURE__ */ jsx(InputLabel, { children: field.label }),
              /* @__PURE__ */ jsx(
                Select,
                {
                  value,
                  onChange: (e) => handleFieldChange(field.name, e.target.value),
                  onBlur: () => handleFieldBlur(field.name),
                  label: field.label,
                  children: field.options?.map((option) => /* @__PURE__ */ jsx(MenuItem, { value: option.value, children: option.label }, option.value))
                }
              ),
              (error || field.helperText) && /* @__PURE__ */ jsx(Typography, { variant: "caption", color: error ? "error" : "text.secondary", sx: { mt: 0.5, ml: 1.75 }, children: error || field.helperText })
            ]
          },
          field.name
        );
      case "multiselect":
        return /* @__PURE__ */ jsxs(
          FormControl,
          {
            fullWidth: true,
            error: !!error,
            required: field.required,
            disabled: field.disabled || loading,
            children: [
              /* @__PURE__ */ jsx(InputLabel, { children: field.label }),
              /* @__PURE__ */ jsx(
                Select,
                {
                  multiple: true,
                  value: value || [],
                  onChange: (e) => handleFieldChange(field.name, e.target.value),
                  onBlur: () => handleFieldBlur(field.name),
                  label: field.label,
                  children: field.options?.map((option) => /* @__PURE__ */ jsx(MenuItem, { value: option.value, children: option.label }, option.value))
                }
              ),
              (error || field.helperText) && /* @__PURE__ */ jsx(Typography, { variant: "caption", color: error ? "error" : "text.secondary", sx: { mt: 0.5, ml: 1.75 }, children: error || field.helperText })
            ]
          },
          field.name
        );
      case "checkbox":
        return /* @__PURE__ */ jsx(
          FormControlLabel,
          {
            control: /* @__PURE__ */ jsx(
              Checkbox,
              {
                checked: value || false,
                onChange: (e) => handleFieldChange(field.name, e.target.checked),
                disabled: field.disabled || loading
              }
            ),
            label: field.label
          },
          field.name
        );
      default:
        return null;
    }
  };
  return /* @__PURE__ */ jsxs(Dialog, { open, onClose, maxWidth, fullWidth: true, children: [
    /* @__PURE__ */ jsx(DialogTitle, { children: title }),
    /* @__PURE__ */ jsxs(DialogContent, { children: [
      description && /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: description }),
      /* @__PURE__ */ jsx(Box, { sx: { pt: 2, display: "flex", flexDirection: "column", gap: 2 }, children: fields.map(renderField) })
    ] }),
    /* @__PURE__ */ jsxs(DialogActions, { children: [
      /* @__PURE__ */ jsx(Button, { onClick: onClose, disabled: loading, children: cancelButtonText }),
      /* @__PURE__ */ jsx(Button, { onClick: handleSubmit, variant: "contained", disabled: loading, children: loading ? /* @__PURE__ */ jsx(CircularProgress, { size: 24 }) : submitButtonText })
    ] })
  ] });
}
function DataTable({
  columns,
  data,
  containerPaper = true,
  loading = false,
  error = null,
  emptyMessage = "No data available",
  onRefresh,
  onRowClick,
  hover = true,
  stickyHeader = false,
  maxHeight,
  actions,
  getRowKey = (_row, index) => index,
  getRowSx,
  persistence,
  onSortChange
}) {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortReverse, setSortReverse] = useState(false);
  const hasLoadedSettingsRef = useRef(false);
  useEffect(() => {
    if (persistence && !hasLoadedSettingsRef.current) {
      try {
        const stored = persistence.storage.getItem(persistence.key);
        if (stored) {
          const settings = JSON.parse(stored);
          setSortColumn(settings.column);
          setSortReverse(settings.reverse);
          onSortChange?.(settings.column, settings.reverse);
        }
      } catch (error2) {
        console.warn("Failed to load table sort settings:", error2);
      }
      hasLoadedSettingsRef.current = true;
    }
  }, [persistence, onSortChange]);
  useEffect(() => {
    if (persistence && sortColumn && hasLoadedSettingsRef.current) {
      try {
        const settings = {
          column: sortColumn,
          reverse: sortReverse
        };
        persistence.storage.setItem(persistence.key, JSON.stringify(settings));
      } catch (error2) {
        console.warn("Failed to save table sort settings:", error2);
      }
    }
  }, [persistence, sortColumn, sortReverse]);
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      const nextReverse = !sortReverse;
      setSortReverse(nextReverse);
      onSortChange?.(columnId, nextReverse);
    } else {
      setSortColumn(columnId);
      setSortReverse(false);
      onSortChange?.(columnId, false);
    }
  };
  const sortedData = useMemo(() => {
    if (!sortColumn) {
      return data;
    }
    const column = columns.find((col) => col.id === sortColumn);
    if (!column || !column.sortable) {
      return data;
    }
    const sorted = [...data].sort((a, b) => {
      if (column.sortFn) {
        return column.sortFn(a, b);
      }
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return aValue - bValue;
      }
      if (aValue instanceof Date && bValue instanceof Date) {
        return aValue.getTime() - bValue.getTime();
      }
      return String(aValue).localeCompare(String(bValue));
    });
    return sortReverse ? sorted.reverse() : sorted;
  }, [data, sortColumn, sortReverse, columns]);
  const handleRowClick = (clickedRow) => {
    if (onRowClick) {
      onRowClick(clickedRow);
    }
  };
  const renderSortIcon = (columnId) => {
    if (sortColumn !== columnId) {
      return null;
    }
    return sortReverse ? /* @__PURE__ */ jsx(ArrowDownward, { fontSize: "small" }) : /* @__PURE__ */ jsx(ArrowUpward, { fontSize: "small" });
  };
  const renderHeader = () => /* @__PURE__ */ jsx(TableHead, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
    columns.map((column) => /* @__PURE__ */ jsx(
      TableCell,
      {
        align: column.align,
        style: { minWidth: column.minWidth },
        sx: {
          cursor: column.sortable ? "pointer" : "default",
          userSelect: "none",
          "&:hover": column.sortable ? {
            backgroundColor: "action.hover"
          } : {}
        },
        onClick: () => column.sortable && !column.renderHeader && handleSort(column.id),
        children: /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              justifyContent: column.align === "right" ? "flex-end" : column.align === "center" ? "center" : "flex-start"
            },
            children: column.renderHeader ? column.renderHeader() : /* @__PURE__ */ jsxs(Fragment, { children: [
              column.label,
              column.sortable && renderSortIcon(column.id)
            ] })
          }
        )
      },
      column.id
    )),
    actions && /* @__PURE__ */ jsx(TableCell, { align: "right", children: "Actions" })
  ] }) });
  const containerComponent = containerPaper ? Paper : "div";
  if (loading) {
    return /* @__PURE__ */ jsxs(TableContainer, { component: containerComponent, children: [
      /* @__PURE__ */ jsx(Table, { stickyHeader, children: renderHeader() }),
      /* @__PURE__ */ jsx(
        Box,
        {
          sx: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4
          },
          children: /* @__PURE__ */ jsx(CircularProgress, {})
        }
      )
    ] });
  }
  if (error) {
    return /* @__PURE__ */ jsx(TableContainer, { component: containerComponent, children: /* @__PURE__ */ jsxs(
      Box,
      {
        sx: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
          gap: 2
        },
        children: [
          /* @__PURE__ */ jsx(Typography, { color: "error", variant: "body1", children: error }),
          onRefresh && /* @__PURE__ */ jsx(Tooltip, { title: "Refresh", children: /* @__PURE__ */ jsx(IconButton, { onClick: onRefresh, color: "primary", children: /* @__PURE__ */ jsx(Refresh, {}) }) })
        ]
      }
    ) });
  }
  if (data.length === 0) {
    return /* @__PURE__ */ jsx(TableContainer, { component: containerComponent, children: /* @__PURE__ */ jsxs(
      Box,
      {
        sx: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
          gap: 2
        },
        children: [
          /* @__PURE__ */ jsx(Typography, { color: "text.secondary", variant: "body1", children: emptyMessage }),
          onRefresh && /* @__PURE__ */ jsx(Tooltip, { title: "Refresh", children: /* @__PURE__ */ jsx(IconButton, { onClick: onRefresh, color: "primary", children: /* @__PURE__ */ jsx(Refresh, {}) }) })
        ]
      }
    ) });
  }
  return /* @__PURE__ */ jsx(TableContainer, { component: containerComponent, sx: { maxHeight }, children: /* @__PURE__ */ jsxs(Table, { stickyHeader, sx: { width: "100%", tableLayout: "auto" }, children: [
    renderHeader(),
    /* @__PURE__ */ jsx(TableBody, { children: sortedData.map((row, index) => /* @__PURE__ */ jsxs(
      TableRow,
      {
        hover,
        onClick: () => handleRowClick(row),
        sx: {
          cursor: onRowClick ? "pointer" : "default",
          "&:hover": onRowClick ? {
            backgroundColor: "action.hover"
          } : {},
          ...getRowSx ? getRowSx(row, index) : {}
        },
        children: [
          columns.map((column) => {
            const value = row[column.id];
            return /* @__PURE__ */ jsx(TableCell, { align: column.align, style: { minWidth: column.minWidth }, children: column.format ? column.format(value, row) : value }, column.id);
          }),
          actions && /* @__PURE__ */ jsx(TableCell, { align: "right", onClick: (e) => e.stopPropagation(), children: actions(row) })
        ]
      },
      getRowKey(row, index)
    )) })
  ] }) });
}
function LogsPane({
  logs,
  loading = false,
  emptyMessage = "No logs available",
  formatTimestamp = (ts) => new Date(ts).toLocaleTimeString(),
  maxHeight = "calc(100vh - 300px)",
  autoScroll = true,
  reverseOrder = true,
  backgroundColor = "var(--palette-primary-75)",
  loadingMessage = "Loading logs..."
}) {
  const logsEndRef = useRef(null);
  useEffect(() => {
    if (autoScroll && logsEndRef.current && logs.length > 0) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, autoScroll]);
  if (loading) {
    return /* @__PURE__ */ jsx(
      Paper,
      {
        sx: {
          backgroundColor,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          maxHeight,
          borderLeft: "2px solid var(--palette-primary-100)"
        },
        children: /* @__PURE__ */ jsxs(
          Box,
          {
            sx: {
              p: 4,
              textAlign: "center",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center"
            },
            children: [
              /* @__PURE__ */ jsx(CircularProgress, { sx: { color: "white", mb: 2 } }),
              /* @__PURE__ */ jsx(Typography, { variant: "h6", sx: { color: "rgba(255, 255, 255, 0.7)" }, children: loadingMessage })
            ]
          }
        )
      }
    );
  }
  if (logs.length === 0) {
    return /* @__PURE__ */ jsx(
      Paper,
      {
        sx: {
          backgroundColor,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          maxHeight,
          borderLeft: "2px solid var(--palette-primary-100)"
        },
        children: /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              p: 4,
              textAlign: "center",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            },
            children: /* @__PURE__ */ jsx(Typography, { variant: "h6", sx: { color: "rgba(255, 255, 255, 0.5)", mb: 2 }, children: emptyMessage })
          }
        )
      }
    );
  }
  const displayLogs = reverseOrder ? [...logs].reverse() : logs;
  return /* @__PURE__ */ jsx(
    Paper,
    {
      sx: {
        backgroundColor,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        maxHeight,
        borderLeft: "2px solid var(--palette-primary-100)"
      },
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          sx: {
            flex: 1,
            overflow: "auto",
            p: 1,
            fontFamily: "monospace",
            fontSize: "0.8rem",
            lineHeight: 1.6,
            minHeight: 0
          },
          children: [
            displayLogs.map((log, index) => /* @__PURE__ */ jsx(
              LogLine,
              {
                message: log.message,
                timestamp: log.timestamp,
                level: log.level,
                service: log.service,
                container: log.container,
                isEvent: log.source === "event",
                formatTimestamp,
                showBorder: index < displayLogs.length - 1
              },
              index
            )),
            /* @__PURE__ */ jsx("div", { ref: logsEndRef })
          ]
        }
      )
    }
  );
}
function LogsSourcePicker({
  sources,
  onSelectSource,
  emptyMessage = "No log sources available",
  ContainerIcon: CustomContainerIcon = /* @__PURE__ */ jsx(Storage, {}),
  BuildIcon: CustomBuildIcon = /* @__PURE__ */ jsx(Build, {}),
  renderStatus
}) {
  const containers = sources.filter((s) => s.type === "container");
  const builds = sources.filter((s) => s.type === "build");
  const initContainers = sources.filter((s) => s.type === "init-container");
  const defaultRenderStatus = (source) => /* @__PURE__ */ jsx(StatusChip, { status: source.status, size: "small" });
  const statusRenderer = renderStatus || defaultRenderStatus;
  if (sources.length === 0) {
    return /* @__PURE__ */ jsx(Paper, { sx: { backgroundColor: "var(--palette-primary-75)", p: 4, textAlign: "center" }, children: /* @__PURE__ */ jsx(Typography, { variant: "h6", sx: { color: "rgba(255, 255, 255, 0.5)", mb: 2 }, children: emptyMessage }) });
  }
  return /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 3 }, children: [
    containers.length > 0 && /* @__PURE__ */ jsxs(Paper, { sx: { backgroundColor: "var(--palette-primary-75)", p: 2 }, children: [
      /* @__PURE__ */ jsxs(Typography, { variant: "h6", sx: { color: "white", mb: 2, display: "flex", alignItems: "center", gap: 1 }, children: [
        CustomContainerIcon,
        "Running Containers"
      ] }),
      /* @__PURE__ */ jsx(TableContainer, { children: /* @__PURE__ */ jsxs(Table, { children: [
        /* @__PURE__ */ jsx(TableHead, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Container Name" }),
          /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Status" }),
          /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Action" })
        ] }) }),
        /* @__PURE__ */ jsx(TableBody, { children: containers.map((source) => /* @__PURE__ */ jsxs(TableRow, { hover: true, children: [
          /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: source.name }),
          /* @__PURE__ */ jsx(TableCell, { children: statusRenderer(source) }),
          /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(
            Link,
            {
              component: "button",
              variant: "body2",
              onClick: () => onSelectSource(source),
              sx: {
                color: "primary.main",
                cursor: "pointer",
                textDecoration: "underline"
              },
              children: "View Logs"
            }
          ) })
        ] }, source.id)) })
      ] }) })
    ] }),
    builds.length > 0 && /* @__PURE__ */ jsxs(Paper, { sx: { backgroundColor: "#1a1a1a", p: 2 }, children: [
      /* @__PURE__ */ jsxs(Typography, { variant: "h6", sx: { color: "white", mb: 2, display: "flex", alignItems: "center", gap: 1 }, children: [
        CustomBuildIcon,
        "Build Logs"
      ] }),
      /* @__PURE__ */ jsx(TableContainer, { children: /* @__PURE__ */ jsxs(Table, { children: [
        /* @__PURE__ */ jsx(TableHead, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Build Service" }),
          /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Status" }),
          /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Action" })
        ] }) }),
        /* @__PURE__ */ jsx(TableBody, { children: builds.map((source) => /* @__PURE__ */ jsxs(TableRow, { hover: true, children: [
          /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: source.name }),
          /* @__PURE__ */ jsx(TableCell, { children: statusRenderer(source) }),
          /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(
            Link,
            {
              component: "button",
              variant: "body2",
              onClick: () => onSelectSource(source),
              sx: {
                color: "primary.main",
                cursor: "pointer",
                textDecoration: "underline"
              },
              children: "View Logs"
            }
          ) })
        ] }, source.id)) })
      ] }) })
    ] }),
    initContainers.length > 0 && /* @__PURE__ */ jsxs(Paper, { sx: { backgroundColor: "#1a1a1a", p: 2 }, children: [
      /* @__PURE__ */ jsxs(Typography, { variant: "h6", sx: { color: "white", mb: 2, display: "flex", alignItems: "center", gap: 1 }, children: [
        CustomContainerIcon,
        "Init Container Logs"
      ] }),
      initContainers[0].description && /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { color: "rgba(255, 255, 255, 0.7)", mb: 2 }, children: initContainers[0].description }),
      initContainers.length === 1 && !initContainers[0].name ? (
        // Single generic init container entry (no specific services)
        /* @__PURE__ */ jsx(
          Link,
          {
            component: "button",
            variant: "body2",
            onClick: () => onSelectSource(initContainers[0]),
            sx: {
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline"
            },
            children: "View All Init Container Logs"
          }
        )
      ) : (
        // Table of specific init containers
        /* @__PURE__ */ jsx(TableContainer, { children: /* @__PURE__ */ jsxs(Table, { children: [
          /* @__PURE__ */ jsx(TableHead, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
            /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Init Container" }),
            /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Status" }),
            /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: "Action" })
          ] }) }),
          /* @__PURE__ */ jsx(TableBody, { children: initContainers.map((source) => /* @__PURE__ */ jsxs(TableRow, { hover: true, children: [
            /* @__PURE__ */ jsx(TableCell, { sx: { color: "white" }, children: source.name }),
            /* @__PURE__ */ jsx(TableCell, { children: statusRenderer(source) }),
            /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(
              Link,
              {
                component: "button",
                variant: "body2",
                onClick: () => onSelectSource(source),
                sx: {
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline"
                },
                children: "View Logs"
              }
            ) })
          ] }, source.id)) })
        ] }) })
      )
    ] })
  ] });
}
function BillingPlansTable({
  plans,
  features,
  currentPlanKey,
  onSelectPlan,
  actionButtonLabel = "Get started",
  disableCurrentPlanAction = false,
  footerText,
  checkIcon,
  renderFalseValue
}) {
  const getButtonLabel = (plan, isCurrent) => {
    if (typeof actionButtonLabel === "function") {
      return actionButtonLabel(plan, isCurrent);
    }
    return actionButtonLabel;
  };
  const renderFeatureValue = (value) => {
    if (value === true) {
      return checkIcon || /* @__PURE__ */ jsx(CheckCircle, { sx: { color: "#cfd8e6" } });
    }
    if (value === false) {
      return renderFalseValue ? renderFalseValue() : null;
    }
    return /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { color: "rgba(230, 237, 246, 0.8)" }, children: value });
  };
  return /* @__PURE__ */ jsx(Box, { sx: { maxWidth: 1200, mx: "auto" }, children: /* @__PURE__ */ jsx(TableContainer, { sx: { padding: 0, backgroundColor: "transparent", border: "none" }, children: /* @__PURE__ */ jsxs(
    Table,
    {
      sx: {
        backgroundColor: "transparent",
        borderCollapse: "separate",
        borderSpacing: 0,
        "& .MuiTableCell-root": {
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          color: "#e6edf6",
          py: 2
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          borderBottom: "none"
        },
        "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
          borderBottom: "none"
        }
      },
      children: [
        /* @__PURE__ */ jsxs(TableHead, { sx: { backgroundColor: "transparent !important" }, children: [
          /* @__PURE__ */ jsxs(TableRow, { sx: { backgroundColor: "transparent !important" }, children: [
            /* @__PURE__ */ jsx(
              TableCell,
              {
                sx: { width: 280, borderBottom: "none", backgroundColor: "transparent" }
              }
            ),
            plans.map((plan) => {
              const isCurrent = currentPlanKey === plan.key;
              return /* @__PURE__ */ jsx(
                TableCell,
                {
                  align: "center",
                  sx: {
                    borderBottom: "none",
                    backgroundColor: "transparent !important",
                    border: "none !important",
                    paddingBottom: "0px !important"
                  },
                  children: isCurrent && /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "overline",
                      sx: {
                        fontWeight: 700,
                        color: "#f2f6ff",
                        letterSpacing: 1,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        paddingLeft: "30px",
                        paddingRight: "30px",
                        backgroundColor: "rgba(255, 255, 255, 0.18)",
                        bottom: 0
                      },
                      children: "Current Plan"
                    }
                  )
                },
                `${plan.key}-current`
              );
            })
          ] }),
          /* @__PURE__ */ jsxs(TableRow, { sx: { backgroundColor: "transparent !important" }, children: [
            /* @__PURE__ */ jsx(
              TableCell,
              {
                sx: { width: 280, borderBottom: "none", backgroundColor: "transparent !important" }
              }
            ),
            plans.map((plan) => {
              const isCurrent = currentPlanKey === plan.key;
              return /* @__PURE__ */ jsx(
                TableCell,
                {
                  align: "center",
                  sx: {
                    borderBottom: "none",
                    pt: 4,
                    pb: 4
                  },
                  children: /* @__PURE__ */ jsxs(
                    Box,
                    {
                      sx: { display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 },
                      children: [
                        /* @__PURE__ */ jsx(Typography, { variant: "h6", sx: { fontWeight: 600 }, children: plan.name }),
                        /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "flex-end", gap: 0.5 }, children: [
                          /* @__PURE__ */ jsx(Typography, { variant: "h4", sx: { fontWeight: 700 }, children: plan.price }),
                          plan.priceSuffix && /* @__PURE__ */ jsx(Typography, { variant: "subtitle2", sx: { fontWeight: 600, pb: 0.5 }, children: plan.priceSuffix })
                        ] }),
                        /* @__PURE__ */ jsx(
                          Typography,
                          {
                            variant: "body2",
                            sx: { maxWidth: 220, color: "rgba(230, 237, 246, 0.8)" },
                            children: plan.description
                          }
                        ),
                        onSelectPlan && /* @__PURE__ */ jsx(
                          Button,
                          {
                            variant: "contained",
                            size: "small",
                            disabled: disableCurrentPlanAction && isCurrent,
                            onClick: () => onSelectPlan(plan.key),
                            sx: {
                              mt: 1,
                              backgroundColor: "#cfd8e6",
                              color: "#1f2d3d",
                              "&:hover": { backgroundColor: "#bfc9da" }
                            },
                            children: getButtonLabel(plan, isCurrent)
                          }
                        )
                      ]
                    }
                  )
                },
                plan.key
              );
            })
          ] })
        ] }),
        /* @__PURE__ */ jsx(TableBody, { children: features.map((feature) => /* @__PURE__ */ jsxs(TableRow, { sx: { backgroundColor: "#223447" }, children: [
          /* @__PURE__ */ jsx(
            TableCell,
            {
              sx: { fontWeight: 500, backgroundColor: "#182433", color: "#e6edf6" },
              children: feature.label
            }
          ),
          plans.map((plan) => {
            const value = feature.values[plan.key];
            return /* @__PURE__ */ jsx(
              TableCell,
              {
                align: "center",
                sx: { color: "#e6edf6" },
                children: renderFeatureValue(value)
              },
              `${feature.label}-${plan.key}`
            );
          })
        ] }, feature.label)) }),
        footerText && /* @__PURE__ */ jsx(TableHead, { sx: { backgroundColor: "transparent !important" }, children: /* @__PURE__ */ jsxs(TableRow, { sx: { backgroundColor: "transparent !important" }, children: [
          /* @__PURE__ */ jsx(
            TableCell,
            {
              sx: { borderBottom: "none", backgroundColor: "transparent !important" }
            }
          ),
          /* @__PURE__ */ jsx(
            TableCell,
            {
              colSpan: plans.length,
              sx: { borderBottom: "none", backgroundColor: "transparent !important" },
              children: /* @__PURE__ */ jsx(
                Typography,
                {
                  variant: "caption",
                  sx: {
                    color: "rgba(230, 237, 246, 0.75)",
                    textAlign: "center",
                    marginLeft: "auto",
                    marginRight: "auto",
                    maxWidth: 640,
                    display: "block"
                  },
                  children: footerText
                }
              )
            }
          )
        ] }) })
      ]
    }
  ) }) });
}
function EnvironmentVariablesEditor({
  variables,
  onAdd,
  onUpdate,
  onDelete,
  loading = false,
  error = null,
  emptyMessage = "No environment variables configured yet.",
  emptyDescription = "Add environment variables that will be available to your application in preview environments.",
  deleteConfirmMessage = 'Are you sure you want to delete the environment variable "{key}"?',
  disabled = false,
  showServiceNames = true
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [formData, setFormData] = useState({
    key: "",
    value: "",
    isSecret: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const handleAddNew = () => {
    setEditingVariable(null);
    setFormData({
      key: "",
      value: "",
      isSecret: false
    });
    setShowPassword(false);
    setDialogOpen(true);
  };
  const handleEdit = (envVar) => {
    setEditingVariable(envVar);
    setFormData({
      key: envVar.key,
      value: envVar.value === "***" ? "" : envVar.value,
      // Don't show masked values
      isSecret: envVar.isSecret
    });
    setShowPassword(false);
    setDialogOpen(true);
  };
  const handleDelete = async (envVar) => {
    const confirmMsg = deleteConfirmMessage.replace("{key}", envVar.key);
    if (!window.confirm(confirmMsg)) {
      return;
    }
    try {
      await onDelete(envVar.id);
    } catch (err) {
      console.error("Failed to delete environment variable:", err);
    }
  };
  const handleSubmit = async () => {
    if (!formData.key.trim() || !formData.value.trim()) {
      return;
    }
    try {
      setSubmitting(true);
      if (editingVariable) {
        await onUpdate(editingVariable.id, formData);
      } else {
        await onAdd(formData);
      }
      setDialogOpen(false);
    } catch (err) {
      console.error("Failed to save environment variable:", err);
    } finally {
      setSubmitting(false);
    }
  };
  const handleCancel = () => {
    setDialogOpen(false);
  };
  const getDisplayValue = (envVar) => {
    if (envVar.isSecret) {
      return envVar.value === "***" ? "***" : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022";
    }
    return envVar.value || "(empty)";
  };
  if (loading) {
    return /* @__PURE__ */ jsx(Box, { display: "flex", justifyContent: "center", p: 3, children: /* @__PURE__ */ jsx(CircularProgress, {}) });
  }
  return /* @__PURE__ */ jsxs(Box, { children: [
    /* @__PURE__ */ jsx(Box, { display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, children: /* @__PURE__ */ jsx(Button, { variant: "contained", startIcon: /* @__PURE__ */ jsx(Add, {}), onClick: handleAddNew, disabled, children: "Add Variable" }) }),
    error && /* @__PURE__ */ jsx(Alert, { severity: "error", sx: { mb: 2 }, children: error }),
    variables.length === 0 ? /* @__PURE__ */ jsxs(Paper, { sx: { p: 3, textAlign: "center" }, children: [
      /* @__PURE__ */ jsx(Typography, { color: "textSecondary", children: emptyMessage }),
      /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "textSecondary", sx: { mt: 1 }, children: emptyDescription })
    ] }) : /* @__PURE__ */ jsx(List, { children: variables.map((envVar) => /* @__PURE__ */ jsxs(
      ListItem,
      {
        sx: {
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 1
        },
        children: [
          /* @__PURE__ */ jsx(
            ListItemText,
            {
              primary: /* @__PURE__ */ jsxs(Box, { display: "flex", alignItems: "center", gap: 1, children: [
                /* @__PURE__ */ jsx(Typography, { variant: "body1", fontWeight: "medium", children: envVar.key }),
                showServiceNames && envVar.serviceName && /* @__PURE__ */ jsx(
                  Chip,
                  {
                    label: `Service: ${envVar.serviceName}`,
                    size: "small",
                    color: "primary",
                    variant: "outlined"
                  }
                ),
                envVar.isSecret && /* @__PURE__ */ jsx(Chip, { label: "Secret", size: "small", color: "warning", variant: "outlined" })
              ] }),
              secondary: /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "textSecondary", children: getDisplayValue(envVar) })
            }
          ),
          /* @__PURE__ */ jsxs(ListItemSecondaryAction, { children: [
            /* @__PURE__ */ jsx(IconButton, { edge: "end", onClick: () => handleEdit(envVar), sx: { mr: 1 }, disabled, children: /* @__PURE__ */ jsx(Edit, {}) }),
            /* @__PURE__ */ jsx(IconButton, { edge: "end", onClick: () => handleDelete(envVar), color: "error", disabled, children: /* @__PURE__ */ jsx(Delete, {}) })
          ] })
        ]
      },
      envVar.id
    )) }),
    /* @__PURE__ */ jsxs(Dialog, { open: dialogOpen, onClose: handleCancel, maxWidth: "sm", fullWidth: true, children: [
      /* @__PURE__ */ jsx(DialogTitle, { children: editingVariable ? "Edit Environment Variable" : "Add Environment Variable" }),
      /* @__PURE__ */ jsx(DialogContent, { children: /* @__PURE__ */ jsxs(Box, { sx: { pt: 1 }, children: [
        /* @__PURE__ */ jsx(
          TextField,
          {
            fullWidth: true,
            label: "Variable Name",
            value: formData.key,
            onChange: (e) => setFormData({ ...formData, key: e.target.value }),
            sx: { mb: 2 },
            placeholder: "e.g., DATABASE_URL",
            disabled: submitting
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            fullWidth: true,
            label: "Value",
            type: showPassword ? "text" : "password",
            value: formData.value,
            onChange: (e) => setFormData({ ...formData, value: e.target.value }),
            sx: { mb: 2 },
            placeholder: "e.g., postgresql://user:pass@host:5432/db",
            disabled: submitting,
            InputProps: {
              endAdornment: /* @__PURE__ */ jsx(IconButton, { onClick: () => setShowPassword(!showPassword), edge: "end", children: showPassword ? /* @__PURE__ */ jsx(VisibilityOff, {}) : /* @__PURE__ */ jsx(Visibility, {}) })
            }
          }
        ),
        /* @__PURE__ */ jsx(
          FormControlLabel,
          {
            control: /* @__PURE__ */ jsx(
              Checkbox,
              {
                checked: formData.isSecret,
                onChange: (e) => setFormData({ ...formData, isSecret: e.target.checked }),
                disabled: submitting
              }
            ),
            label: "Mark as secret (value will be hidden in logs and UI)"
          }
        )
      ] }) }),
      /* @__PURE__ */ jsxs(DialogActions, { children: [
        /* @__PURE__ */ jsx(Button, { onClick: handleCancel, disabled: submitting, children: "Cancel" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            onClick: handleSubmit,
            variant: "contained",
            disabled: submitting || !formData.key.trim() || !formData.value.trim(),
            children: submitting ? /* @__PURE__ */ jsx(CircularProgress, { size: 20 }) : editingVariable ? "Update" : "Add"
          }
        )
      ] })
    ] })
  ] });
}
function ResourceLimitsTable({
  limits,
  onUpdateLimit,
  memoryOptions = ["128Mi", "256Mi", "512Mi", "1Gi", "2Gi", "4Gi", "8Gi"],
  cpuOptions = ["100m", "125m", "250m", "500m", "1", "2"],
  loading = false,
  error = null,
  disabled = false,
  emptyMessage = "No services found in compose file. Make sure you have selected a valid compose file."
}) {
  const handleChange = async (serviceName, field, value) => {
    await onUpdateLimit(serviceName, field, value);
  };
  if (loading) {
    return /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", p: 2 }, children: /* @__PURE__ */ jsx(CircularProgress, {}) });
  }
  if (error) {
    return /* @__PURE__ */ jsx(Alert, { severity: "error", sx: { mb: 2 }, children: error });
  }
  if (limits.length === 0) {
    return /* @__PURE__ */ jsx(Alert, { severity: "info", sx: { mb: 2 }, children: emptyMessage });
  }
  return /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(TableContainer, { component: Paper, children: /* @__PURE__ */ jsxs(Table, { children: [
    /* @__PURE__ */ jsxs(TableHead, { children: [
      /* @__PURE__ */ jsxs(TableRow, { children: [
        /* @__PURE__ */ jsx(TableCell, { children: "Service Name" }),
        /* @__PURE__ */ jsx(
          TableCell,
          {
            align: "center",
            colSpan: 2,
            sx: { borderLeft: "2px solid", borderRight: "2px solid", borderColor: "divider" },
            children: /* @__PURE__ */ jsx(Typography, { variant: "subtitle2", sx: { fontWeight: "bold" }, children: "Service Resources" })
          }
        ),
        /* @__PURE__ */ jsx(TableCell, { align: "center", colSpan: 2, sx: { borderLeft: "2px solid", borderColor: "divider" }, children: /* @__PURE__ */ jsx(Typography, { variant: "subtitle2", sx: { fontWeight: "bold" }, children: "Build Resources" }) })
      ] }),
      /* @__PURE__ */ jsxs(TableRow, { children: [
        /* @__PURE__ */ jsx(TableCell, {}),
        /* @__PURE__ */ jsx(TableCell, { sx: { borderLeft: "2px solid", borderColor: "divider" }, children: "Memory" }),
        /* @__PURE__ */ jsx(TableCell, { sx: { borderRight: "2px solid", borderColor: "divider" }, children: "CPU" }),
        /* @__PURE__ */ jsx(TableCell, { sx: { borderLeft: "2px solid", borderColor: "divider" }, children: "Memory" }),
        /* @__PURE__ */ jsx(TableCell, { children: "CPU" })
      ] })
    ] }),
    /* @__PURE__ */ jsx(TableBody, { children: limits.map((limit) => /* @__PURE__ */ jsxs(TableRow, { children: [
      /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: "medium" }, children: limit.serviceName }) }),
      /* @__PURE__ */ jsx(TableCell, { sx: { borderLeft: "2px solid", borderColor: "divider" }, children: /* @__PURE__ */ jsx(FormControl, { size: "small", sx: { minWidth: 120 }, children: /* @__PURE__ */ jsx(
        Select,
        {
          value: limit.serviceMemoryLimit,
          onChange: (e) => handleChange(limit.serviceName, "serviceMemoryLimit", e.target.value),
          disabled,
          children: memoryOptions.map((option) => /* @__PURE__ */ jsx(MenuItem, { value: option, children: option }, option))
        }
      ) }) }),
      /* @__PURE__ */ jsx(TableCell, { sx: { borderRight: "2px solid", borderColor: "divider" }, children: /* @__PURE__ */ jsx(FormControl, { size: "small", sx: { minWidth: 120 }, children: /* @__PURE__ */ jsx(
        Select,
        {
          value: limit.serviceCPULimit,
          onChange: (e) => handleChange(limit.serviceName, "serviceCPULimit", e.target.value),
          disabled,
          children: cpuOptions.map((option) => /* @__PURE__ */ jsx(MenuItem, { value: option, children: option }, option))
        }
      ) }) }),
      /* @__PURE__ */ jsx(TableCell, { sx: { borderLeft: "2px solid", borderColor: "divider" }, children: /* @__PURE__ */ jsx(FormControl, { size: "small", sx: { minWidth: 120 }, children: /* @__PURE__ */ jsx(
        Select,
        {
          value: limit.buildMemoryLimit,
          onChange: (e) => handleChange(limit.serviceName, "buildMemoryLimit", e.target.value),
          disabled,
          children: memoryOptions.map((option) => /* @__PURE__ */ jsx(MenuItem, { value: option, children: option }, option))
        }
      ) }) }),
      /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(FormControl, { size: "small", sx: { minWidth: 120 }, children: /* @__PURE__ */ jsx(
        Select,
        {
          value: limit.buildCPULimit,
          onChange: (e) => handleChange(limit.serviceName, "buildCPULimit", e.target.value),
          disabled,
          children: cpuOptions.map((option) => /* @__PURE__ */ jsx(MenuItem, { value: option, children: option }, option))
        }
      ) }) })
    ] }, limit.serviceName)) })
  ] }) }) });
}
function TemplateVariablesDialog({
  open,
  onClose,
  onSelectVariable,
  variables = [],
  loading = false,
  error = null,
  variableFormat = (name) => `{{${name}}}`
}) {
  const handleSelectVariable = (variable) => {
    onSelectVariable(variableFormat(variable.name));
    onClose();
  };
  return /* @__PURE__ */ jsxs(Dialog, { open, onClose, maxWidth: "md", fullWidth: true, children: [
    /* @__PURE__ */ jsxs(DialogTitle, { children: [
      /* @__PURE__ */ jsx(Typography, { variant: "h6", children: "Available Template Variables" }),
      /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1 }, children: "Click on a variable to insert it into your environment variable value" })
    ] }),
    /* @__PURE__ */ jsxs(DialogContent, { children: [
      loading && /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 2 }, children: /* @__PURE__ */ jsx(CircularProgress, {}) }),
      error && /* @__PURE__ */ jsx(Alert, { severity: "error", sx: { mb: 2 }, children: error }),
      !loading && !error && variables.length === 0 && /* @__PURE__ */ jsx(Typography, { color: "text.secondary", sx: { textAlign: "center", py: 2 }, children: "No template variables available" }),
      !loading && !error && variables.length > 0 && /* @__PURE__ */ jsx(List, { children: variables.map((variable, index) => /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsx(ListItemButton, { onClick: () => handleSelectVariable(variable), children: /* @__PURE__ */ jsx(
        ListItemText,
        {
          primary: /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
            /* @__PURE__ */ jsx(
              Chip,
              {
                label: variableFormat(variable.name),
                size: "small",
                color: "primary",
                variant: "outlined"
              }
            ),
            /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { fontWeight: "medium" }, children: variable.name })
          ] }),
          secondary: /* @__PURE__ */ jsxs(Box, { sx: { mt: 0.5 }, children: [
            /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", children: variable.description }),
            variable.example && /* @__PURE__ */ jsxs(Typography, { variant: "caption", color: "text.secondary", sx: { fontStyle: "italic" }, children: [
              "Example: ",
              variable.example
            ] })
          ] })
        }
      ) }) }, index)) })
    ] }),
    /* @__PURE__ */ jsx(DialogActions, { children: /* @__PURE__ */ jsx(Button, { onClick: onClose, children: "Close" }) })
  ] });
}
function FilePicker({
  open,
  onClose,
  onSelect,
  files = [],
  loading = false,
  error = null,
  currentPath = "",
  onNavigate,
  title = "Select File",
  emptyMessage = "No files found in this directory",
  fileFilter
}) {
  const handleFileClick = (file) => {
    if (file.type === "dir") {
      onNavigate?.(file.path);
    } else {
      onSelect(file.path);
      onClose();
    }
  };
  const handleNavigateUp = () => {
    const parentPath = currentPath.split("/").slice(0, -1).join("/");
    onNavigate?.(parentPath);
  };
  const handleNavigateToPath = (index) => {
    const newPath = pathParts.slice(0, index + 1).join("/");
    onNavigate?.(newPath);
  };
  const pathParts = currentPath.split("/").filter(Boolean);
  const filteredFiles = fileFilter ? files.filter(fileFilter) : files;
  return /* @__PURE__ */ jsxs(Dialog, { open, onClose, maxWidth: "sm", fullWidth: true, children: [
    /* @__PURE__ */ jsx(DialogTitle, { children: /* @__PURE__ */ jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", children: [
      /* @__PURE__ */ jsx(Typography, { variant: "h6", children: title }),
      /* @__PURE__ */ jsx(IconButton, { edge: "end", onClick: onClose, children: /* @__PURE__ */ jsx(Close, {}) })
    ] }) }),
    /* @__PURE__ */ jsxs(DialogContent, { children: [
      /* @__PURE__ */ jsxs(Breadcrumbs$1, { sx: { mb: 2 }, children: [
        /* @__PURE__ */ jsx(
          Link,
          {
            component: "button",
            variant: "body1",
            onClick: () => onNavigate?.(""),
            color: "inherit",
            sx: { cursor: "pointer" },
            children: "Root"
          }
        ),
        pathParts.map((part, index) => /* @__PURE__ */ jsx(
          Link,
          {
            component: "button",
            variant: "body1",
            onClick: () => handleNavigateToPath(index),
            color: "inherit",
            sx: { cursor: "pointer" },
            children: part
          },
          `${part}-${index}`
        ))
      ] }),
      currentPath && /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsxs(ListItemButton, { onClick: handleNavigateUp, children: [
        /* @__PURE__ */ jsx(ListItemIcon, { children: /* @__PURE__ */ jsx(ArrowBack, {}) }),
        /* @__PURE__ */ jsx(ListItemText, { primary: "..." })
      ] }) }),
      loading && /* @__PURE__ */ jsx(Box, { display: "flex", justifyContent: "center", p: 3, children: /* @__PURE__ */ jsx(CircularProgress, {}) }),
      error && /* @__PURE__ */ jsx(Alert, { severity: "error", sx: { mb: 2 }, children: error }),
      !loading && !error && /* @__PURE__ */ jsxs(List, { children: [
        filteredFiles.map((file) => /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsxs(ListItemButton, { onClick: () => handleFileClick(file), children: [
          /* @__PURE__ */ jsx(ListItemIcon, { children: file.type === "dir" ? /* @__PURE__ */ jsx(Folder, {}) : /* @__PURE__ */ jsx(Description, {}) }),
          /* @__PURE__ */ jsx(
            ListItemText,
            {
              primary: file.name,
              secondary: file.type === "dir" ? "Directory" : file.size ? `${file.size} bytes` : void 0
            }
          )
        ] }) }, file.path)),
        filteredFiles.length === 0 && /* @__PURE__ */ jsx(ListItem, { children: /* @__PURE__ */ jsx(ListItemText, { primary: emptyMessage }) })
      ] })
    ] })
  ] });
}
function DeploymentStatusTimeline({
  steps,
  showDuration = true,
  showCompletedAt = true,
  formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1e3);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  },
  formatTimestamp = (ts) => {
    const date = new Date(ts);
    const now = /* @__PURE__ */ new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 6e4);
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  },
  renderStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return /* @__PURE__ */ jsx(CheckCircle, { sx: { color: "var(--palette-primary-600)" } });
      case "failed":
        return /* @__PURE__ */ jsx(Error$1, { sx: { color: "var(--palette-warning-400)" } });
      case "running":
        return /* @__PURE__ */ jsx(PlayArrow, { sx: { color: "var(--palette-primary-600)" } });
      case "pending":
        return /* @__PURE__ */ jsx(Schedule, { sx: { color: "var(--palette-secondary2-300)" } });
      default:
        return null;
    }
  },
  emptyMessage = "No deployment steps available"
}) {
  if (steps.length === 0) {
    return /* @__PURE__ */ jsx(Paper, { sx: { p: 3, textAlign: "center" }, children: /* @__PURE__ */ jsx(Typography, { color: "textSecondary", children: emptyMessage }) });
  }
  return /* @__PURE__ */ jsx(TableContainer, { component: Paper, children: /* @__PURE__ */ jsxs(Table, { children: [
    /* @__PURE__ */ jsx(TableHead, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
      /* @__PURE__ */ jsx(TableCell, { sx: { width: "50%" }, children: "Step" }),
      /* @__PURE__ */ jsx(TableCell, { align: "right", children: "Status" }),
      showDuration && /* @__PURE__ */ jsx(TableCell, { align: "right", children: "Duration" }),
      showCompletedAt && /* @__PURE__ */ jsx(TableCell, { align: "right", children: "Completed At" })
    ] }) }),
    /* @__PURE__ */ jsx(TableBody, { children: steps.map((step) => {
      const isCompleted = step.status === "completed";
      const isFailed = step.status === "failed";
      const isPending = step.status === "pending";
      return /* @__PURE__ */ jsxs(
        TableRow,
        {
          sx: {
            backgroundColor: isCompleted ? "rgba(99, 163, 202, 0.04)" : isFailed ? "rgba(204, 104, 94, 0.08)" : isPending ? "rgba(227, 188, 131, 0.04)" : "rgba(1, 71, 135, 0.08)",
            // Primary-300 with opacity (active/running)
            borderLeft: isCompleted ? "2px solid rgba(99, 163, 202, 0.3)" : isFailed ? "4px solid var(--palette-warning-400)" : isPending ? "2px solid rgba(227, 188, 131, 0.3)" : "4px solid var(--palette-primary-300)",
            // #014787 - active/running
            my: 1,
            opacity: isCompleted || isPending ? 0.7 : 1
            // Dim completed/pending steps
          },
          children: [
            /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
              renderStatusIcon(step.status),
              /* @__PURE__ */ jsxs(Box, { children: [
                /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontFamily: "monospace" }, children: step.name }),
                step.phase && /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "textSecondary", children: step.phase })
              ] })
            ] }) }),
            /* @__PURE__ */ jsx(TableCell, { align: "right", children: /* @__PURE__ */ jsx(
              Chip,
              {
                label: isCompleted ? "Completed" : isFailed ? "Failed" : isPending ? "Pending" : "Running",
                size: "small",
                sx: {
                  backgroundColor: isCompleted ? "var(--palette-primary-600)" : isFailed ? "var(--palette-warning-400)" : isPending ? "var(--palette-secondary2-300)" : "var(--palette-primary-600)",
                  // #63A3CA - Blue for running
                  color: "#FFFFFF"
                }
              }
            ) }),
            showDuration && /* @__PURE__ */ jsx(TableCell, { align: "right", children: step.duration !== void 0 ? formatDuration(step.duration) : "-" }),
            showCompletedAt && /* @__PURE__ */ jsx(TableCell, { align: "right", children: step.completedAt ? formatTimestamp(step.completedAt) : step.failedAt ? formatTimestamp(step.failedAt) : "-" })
          ]
        },
        step.id
      );
    }) })
  ] }) });
}
function DatabaseConnectionInfo({
  connection,
  showConnectionString = true,
  maskPassword = true,
  onCopy,
  title = "Connection Details",
  readOnly = false
}) {
  const [passwordVisible, setPasswordVisible] = useState(!maskPassword);
  const handleCopy = (field, value) => {
    navigator.clipboard.writeText(value);
    onCopy?.(field, value);
  };
  const renderField = (label, value, fieldName, isPassword = false) => /* @__PURE__ */ jsxs(Box, { sx: { mb: 2 }, children: [
    /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", sx: { mb: 0.5, display: "block" }, children: label }),
    /* @__PURE__ */ jsx(
      TextField,
      {
        fullWidth: true,
        value,
        type: isPassword && !passwordVisible ? "password" : "text",
        InputProps: {
          readOnly: true,
          sx: { fontFamily: "monospace", fontSize: "0.875rem" },
          endAdornment: /* @__PURE__ */ jsxs(InputAdornment, { position: "end", children: [
            isPassword && /* @__PURE__ */ jsx(
              IconButton,
              {
                onClick: () => setPasswordVisible(!passwordVisible),
                edge: "end",
                size: "small",
                children: passwordVisible ? /* @__PURE__ */ jsx(VisibilityOff, {}) : /* @__PURE__ */ jsx(Visibility, {})
              }
            ),
            !readOnly && /* @__PURE__ */ jsx(Tooltip, { title: `Copy ${label}`, children: /* @__PURE__ */ jsx(IconButton, { onClick: () => handleCopy(fieldName, value), edge: "end", size: "small", children: /* @__PURE__ */ jsx(ContentCopy, { fontSize: "small" }) }) })
          ] })
        }
      }
    )
  ] });
  return /* @__PURE__ */ jsxs(Paper, { sx: { p: 3 }, children: [
    /* @__PURE__ */ jsx(Typography, { variant: "h6", sx: { mb: 3 }, children: title }),
    renderField("Host", connection.host, "host"),
    renderField("Port", String(connection.port), "port"),
    renderField("Database", connection.database, "database"),
    renderField("Username", connection.username, "username"),
    renderField("Password", connection.password, "password", true),
    connection.sslMode && renderField("SSL Mode", connection.sslMode, "sslMode"),
    showConnectionString && connection.connectionString && /* @__PURE__ */ jsxs(Box, { sx: { mt: 3 }, children: [
      /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", sx: { mb: 0.5, display: "block" }, children: "Connection String" }),
      /* @__PURE__ */ jsx(
        TextField,
        {
          fullWidth: true,
          multiline: true,
          rows: 2,
          value: connection.connectionString,
          InputProps: {
            readOnly: true,
            sx: { fontFamily: "monospace", fontSize: "0.875rem" },
            endAdornment: !readOnly && /* @__PURE__ */ jsx(InputAdornment, { position: "end", sx: { alignSelf: "flex-start", mt: 1 }, children: /* @__PURE__ */ jsx(Tooltip, { title: "Copy Connection String", children: /* @__PURE__ */ jsx(
              IconButton,
              {
                onClick: () => handleCopy("connectionString", connection.connectionString),
                edge: "end",
                size: "small",
                children: /* @__PURE__ */ jsx(ContentCopy, { fontSize: "small" })
              }
            ) }) })
          }
        }
      )
    ] })
  ] });
}

// src/index.ts
var version = "0.1.0";

export { BillingPlansTable, Breadcrumbs, ConfirmationDialog, DEFAULT_THEME_NAME, DEFAULT_THEME_STORAGE_KEY, DataTable, DatabaseConnectionInfo, DeploymentStatusTimeline, EmptyState, EnvironmentVariablesEditor, FilePicker, Inline, ListManagerModal, LoadingState, LogLine, LogsPane, LogsSourcePicker, MultiFieldFormModal, ResourceLimitsTable, StandardButton, StatCard, StatusChip, TemplateVariablesDialog, TextInputModal, Toast, VeilStreamThemeProvider, detectLogLevel, getLogLevelColor, getTheme, localStorageAdapter, noOpStorageAdapter, stripIsoTimestampPrefix, themeNames, themes, useVeilStreamTheme, version };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map